import { useEffect, useRef, useState } from "preact/hooks";
import { HISTORY_DAYS } from "../engine";
import { api, ApiError, getSession, isPrimary, type BackupInfo, type Session } from "../api";
import { openStore } from "../db";
import { nativePlatform } from "../platform";
import {
  BACKUP_ITERATIONS,
  exportPublic,
  importHistory,
  isPhone,
  linkCode,
  linkKey,
  linkURI,
  normCode,
  newLinkKeys,
  parseLinkURI,
  backupHeader,
  packBackup,
  passwordKey,
  seal,
  unpackBackup,
  unseal,
} from "../history";
import { APPLE_SIGN_IN_ID, clouds, saveBackupFile, type Cloud, type CloudInfo, type CloudKind } from "../cloud";
import * as S from "../state";
import qrcode from "../vendor/qrcode/qrcode.js";
import { Icon } from "./Icon";
import { drawer, engine, toast } from "./Main";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function QRCodeSVG({ text, size = 264 }: { text: string; size?: number }) {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 4;
  let d = "";
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + quiet} ${r + quiet}h1v1h-1z`;
  const dim = n + quiet * 2;
  return (
    <svg class="qr" width={size} height={size} viewBox={`0 0 ${dim} ${dim}`} role="img" aria-label="QR code" shape-rendering="crispEdges">
      <rect width={dim} height={dim} fill="#fff" />
      <path d={d} fill="#111b21" />
    </svg>
  );
}

// ---- new device: show a QR code, wait for approval ---------------------------------

export function LinkLogin({ onCancel, smsLogin }: { onCancel?: () => void; smsLogin?: () => void }) {
  // A phone linking itself to the main phone (companion mode).
  const companion = isPhone(nativePlatform);
  // QR code, or (no camera handy) the phone number and a code typed on the phone.
  const [mode, setMode] = useState<"qr" | "number">("qr");
  const [cc, setCc] = useState("+91");
  const [number, setNumber] = useState("");
  const [phone, setPhone] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [status, setStatus] = useState("Generating code…");
  const [error, setError] = useState("");
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (mode === "number" && !phone) return;
    let live = true;
    setCode(null);
    (async () => {
      try {
        setError("");
        const keys = await newLinkKeys();
        const pub = await exportPublic(keys.publicKey);
        const req = mode === "qr" ? await api.createLink() : await api.createLink({ phone: phone!, pub });
        if (!live) return;
        setCode(mode === "qr" ? linkURI(req.id, pub) : await linkCode(pub));
        setStatus("Waiting for your phone…");
        const deadline = Date.now() + req.expires_in * 1000;
        while (live && Date.now() < deadline) {
          const r = await api.pollLink(req.id, req.token);
          if (r.status !== "approved") continue;
          setStatus("Linked! Loading your chats…");
          await receiveHistory(r.session, r.transfer, keys.privateKey);
          api.adopt(r.session); // switches to the app
          return;
        }
        if (live) setRound((n) => n + 1); // expired: fresh code
      } catch (e) {
        if (!live) return;
        if (e instanceof ApiError && e.code === "expired") setRound((n) => n + 1);
        else if (mode === "number" && e instanceof ApiError && e.status !== 429) (setPhone(null), setError(errText(e)));
        else setError(errText(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [round, mode, phone]);

  const switchTo = (m: "qr" | "number") => (setMode(m), setPhone(null), setCode(null), setError(""), setStatus("Generating code…"));

  return (
    <div class="auth">
      <div class="auth-card link-login">
        <div class="auth-logo">
          <img src="/icon.svg" alt="" width={48} height={48} />
          <h1>xmm</h1>
        </div>
        {mode === "qr" ? (
          <>
            <h2>{companion ? "Link this phone as a companion" : "Use xmm on your computer"}</h2>
            <ol class="muted">
              <li>Open xmm on your {companion ? "main " : ""}phone</li>
              <li>
                Go to <strong>Menu → Linked devices → Link a device</strong>
              </li>
              <li>Point your phone at this screen to scan the QR code</li>
            </ol>
            {code ? <QRCodeSVG text={code} /> : <div class="qr-placeholder" />}
            <p class="muted small">{status}</p>
            {code && (
              <details>
                <summary>Can't scan? Copy the code</summary>
                <textarea class="link-code" readOnly value={code} aria-label="Link code" onFocus={(e) => e.currentTarget.select()} />
              </details>
            )}
            {error && <p class="error">{error}</p>}
            <button class="link" onClick={() => switchTo("number")}>
              Link with phone number instead
            </button>
          </>
        ) : !phone ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError("");
              setPhone(cc + number);
            }}
          >
            <h2>Enter your phone number</h2>
            <p class="muted">The one you use xmm with on your phone. You'll type a code there; no SMS is sent.</p>
            <div class="phone-row">
              <input class="cc" value={cc} onInput={(e) => setCc(e.currentTarget.value)} aria-label="Country code" inputMode="tel" />
              <input value={number} onInput={(e) => setNumber(e.currentTarget.value)} placeholder="Phone number" aria-label="Phone number" inputMode="tel" autoComplete="tel-national" />
            </div>
            {error && <p class="error">{error}</p>}
            <button class="primary" disabled={number.replace(/\D/g, "").length < 6}>
              Next
            </button>
            <button type="button" class="link" onClick={() => switchTo("qr")}>
              Link with a QR code instead
            </button>
          </form>
        ) : (
          <>
            <h2>Enter this code on your phone</h2>
            <div class="link-number-code" aria-label="Linking code">
              {code ? (
                <>
                  <span>{code.slice(0, 4)}</span>
                  <span>{code.slice(4)}</span>
                </>
              ) : (
                <span class="muted">…</span>
              )}
            </div>
            <ol class="muted">
              <li>Open xmm on your phone ({phone}): it shows a notification</li>
              <li>
                Or go to <strong>Menu → Linked devices → Link a device → Link with phone number instead</strong>
              </li>
              <li>Enter the code above</li>
            </ol>
            <p class="muted small">{status}</p>
            {error && <p class="error">{error}</p>}
            <button class="link" onClick={() => switchTo("qr")}>
              Link with a QR code instead
            </button>
          </>
        )}
        <p class="muted small">
          <Icon name="lock" size={12} /> Your phone sends your chat history encrypted; the server can't read it.
        </p>
        {smsLogin && (
          <button class="link" onClick={smsLogin}>
            {companion ? "Make this my main phone instead" : "Log in with an SMS code (test)"}
          </button>
        )}
        {onCancel && (
          <button class="link" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

async function receiveHistory(session: Session, transfer: { url: string; pub: string } | null, priv: CryptoKey) {
  // Linked by QR from another device: this is the history handover.
  const store = await openStore(session.xmpp.jid);
  await store.put("kv", true, "history:done");
  await store.put("kv", true, "backup:checked"); // its history is the phone's, not the server backup
  store.close();
  if (!transfer) return;
  const key = await linkKey(priv, transfer.pub);
  const res = await fetch(transfer.url);
  const bundle = await unseal(key, await res.arrayBuffer());
  const db = await openStore(session.xmpp.jid);
  try {
    await importHistory(db, bundle, session.xmpp.jid);
    if (bundle.complete) await db.put("kv", true, "history:full");
  } finally {
    db.close();
  }
}

// ---- logged-in device: scan and approve -------------------------------------------------

export function LinkScanner({ onClose, startByCode = false }: { onClose: () => void; startByCode?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState("");
  // "Link with phone number": type the code the new device shows.
  const [byCode, setByCode] = useState(startByCode);
  const [typed, setTyped] = useState("");
  const [camera, setCamera] = useState<"starting" | "on" | "unsupported">("starting");
  const [pending, setPending] = useState<{ id: string; pub: string; device: string; phone: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    let stream: MediaStream | null = null;
    let raf = 0;
    const Detector = (window as unknown as { BarcodeDetector?: new (o: object) => { detect(v: HTMLVideoElement): Promise<{ rawValue: string }[]> } }).BarcodeDetector;
    (async () => {
      if (!Detector || !navigator.mediaDevices) return setCamera("unsupported");
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        const v = video.current!;
        v.srcObject = stream;
        await v.play();
        setCamera("on");
        const detector = new Detector({ formats: ["qr_code"] });
        const scan = async () => {
          const codes = await detector.detect(v).catch(() => []);
          const hit = codes.map((c) => c.rawValue).find((c) => parseLinkURI(c));
          if (hit) return void found(hit);
          raf = requestAnimationFrame(() => void scan());
        };
        void scan();
      } catch {
        setCamera("unsupported");
      }
    })();
    return () => {
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function found(text: string) {
    const p = parseLinkURI(text);
    if (!p) return setError("That isn't an xmm link code.");
    try {
      const info = await api.linkInfo(p.id);
      setPending({ ...p, device: info.device_name || info.platform || "a new device", phone: isPhone(info.platform) });
    } catch (e) {
      setError(errText(e));
    }
  }

  async function findByCode() {
    setError("");
    setBusy(true);
    try {
      const code = normCode(typed);
      const r = await api.linkByCode(code);
      // The code is a hash of the new device's key: check we got that key.
      if ((await linkCode(r.pub)) !== code) throw new Error("That code doesn't match: try again on the new device.");
      setPending({ id: r.id, pub: r.pub, device: r.device_name || r.platform || "a new device", phone: isPhone(r.platform) });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!pending) return;
    setBusy(true);
    setError("");
    try {
      // Seal our history for the new device with a key only the two of us
      // know: everything for the phone app, the last 30 days for the web.
      const mine = await newLinkKeys();
      const key = await linkKey(mine.privateKey, pending.pub);
      const e = engine.value!;
      const all = pending.phone && !!(await e.hasFullHistory());
      const blob = await seal(key, all ? { ...(await e.exportAllHistory()), complete: true } : await e.exportHistory());
      const up = await api.upload(blob, "history.bin");
      await api.approveLink(pending.id, { url: up.url, pub: await exportPublic(mine.publicKey), size: up.size });
      toast(`${pending.device} linked`);
      S.devicesChanged.value++;
      onClose();
    } catch (e) {
      setError(errText(e));
      setBusy(false);
    }
  }

  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal link-scanner" role="dialog" aria-label="Link a device" onClick={(e) => e.stopPropagation()}>
        <h2>Link a device</h2>
        {pending ? (
          <>
            <p>
              Link <strong>{pending.device}</strong> to your account? It will get your chat history and receive new messages.
            </p>
            {error && <p class="error">{error}</p>}
            <div class="row-buttons">
              <button class="link" onClick={onClose}>
                Cancel
              </button>
              <button class="primary" disabled={busy} onClick={() => void approve()}>
                {busy ? "Linking…" : "Link device"}
              </button>
            </div>
          </>
        ) : (
          byCode ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void findByCode();
              }}
            >
              <p class="muted small">Enter the 8-character code shown on the device you're linking.</p>
              <input
                class="line-input link-code-input"
                value={typed}
                maxLength={9}
                autoCapitalize="characters"
                autoComplete="off"
                placeholder="XXXX-XXXX"
                aria-label="Linking code"
                onInput={(e) => setTyped(e.currentTarget.value.toUpperCase())}
              />
              {error && <p class="error">{error}</p>}
              <div class="row-buttons">
                <button type="button" class="link" onClick={() => (setByCode(false), setError(""))}>
                  Scan a QR code instead
                </button>
                <button class="primary" disabled={busy || normCode(typed).length !== 8}>
                  Continue
                </button>
              </div>
            </form>
          ) : (
          <>
            {camera !== "unsupported" ? (
              <video ref={video} class="scanner-video" muted playsInline />
            ) : (
              <p class="muted small">This browser can't scan QR codes. Paste the code shown on the new device instead.</p>
            )}
            <textarea class="link-code" value={manual} onInput={(e) => setManual(e.currentTarget.value)} placeholder="xmm-link:v1:…" aria-label="Paste link code" />
            {error && <p class="error">{error}</p>}
            <div class="row-buttons">
              <button class="link" onClick={() => (setByCode(true), setError(""))}>
                Link with phone number instead
              </button>
              <button class="link" onClick={onClose}>
                Cancel
              </button>
              <button class="primary" disabled={!manual.trim()} onClick={() => void found(manual)}>
                Continue
              </button>
            </div>
          </>
          )
        )}
      </div>
    </div>
  );
}

/** Phone: a computer asked to link by code (it showed the user a code). */
export function LinkCodePrompt() {
  const req = S.linkCodeRequest.value;
  const [entering, setEntering] = useState(false);
  useEffect(() => {
    if (!req) return;
    const t = setTimeout(() => (S.linkCodeRequest.value = null), Math.max(0, req.at + 180_000 - Date.now()));
    return () => clearTimeout(t);
  }, [req]);
  if (!req || !isPrimary()) return null;
  const close = () => ((S.linkCodeRequest.value = null), setEntering(false));
  if (entering) return <LinkScanner startByCode onClose={close} />;
  return (
    <div class="modal-backdrop">
      <div class="modal link-prompt" role="dialog" aria-label="Link a device?">
        <h2>Link a device?</h2>
        <p>
          <strong>{req.device || "A device"}</strong> wants to link to your account. Enter the code it shows to link it.
        </p>
        <p class="muted small">Didn't ask for this? Ignore it: nobody can link without that code.</p>
        <div class="row-buttons">
          <button class="link" onClick={close}>
            Not now
          </button>
          <button class="primary" onClick={() => setEntering(true)}>
            Enter code
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- encrypted chat backup ---------------------------------------------------------------

type Dest = "xmm" | "file" | CloudKind;

/** Encrypted chat backups: to the xmm server, the user's Google Drive or
 * iCloud, or a file. All encrypted with a password only the user knows. */
export function BackupPanel() {
  const [info, setInfo] = useState<BackupInfo | null | undefined>(undefined);
  const [cl, setCl] = useState<Cloud[]>([]);
  const [cloudInfo, setCloudInfo] = useState<Partial<Record<CloudKind, CloudInfo | null>>>({});
  const [target, setTarget] = useState<{ dest: Dest; mode: "backup" | "restore" } | null>(null);
  const [file, setFile] = useState<ArrayBuffer | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const account = getSession()?.xmpp.jid ?? "";
  const load = () => api.backup().then((r) => setInfo(r.backup), () => setInfo(null));
  useEffect(() => {
    void load();
    void clouds().then(setCl);
  }, []);
  const cloud = (k: Dest) => cl.find((c) => c.kind === k);
  const close = () => (setTarget(null), setPw(""), setPw2(""), setFile(null));

  /** From a click: a cloud may need to sign in first (popup). */
  async function start(dest: Dest, mode: "backup" | "restore") {
    setError("");
    const c = cloud(dest);
    if (!c) return setTarget({ dest, mode });
    setTarget({ dest, mode });
    setBusy(true);
    try {
      await c.connect();
      const i = await c.info(account);
      setCloudInfo((x) => ({ ...x, [c.kind]: i }));
      if (mode === "restore" && !i) setError(`There's no backup of this account in your ${c.label} yet.`);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function pickFile(f: File | undefined) {
    if (!f) return;
    setError("");
    try {
      const data = await f.arrayBuffer();
      const { header } = backupHeader(data);
      if (header.account !== account) throw new Error("this backup belongs to another account");
      setFile(data);
      setTarget({ dest: "file", mode: "restore" });
    } catch (e) {
      toast(errText(e));
    }
  }

  async function submit() {
    if (!target) return;
    const { dest, mode } = target;
    setError("");
    if (mode === "backup" && pw.length < 8) return setError("Use at least 8 characters.");
    if (mode === "backup" && pw !== pw2) return setError("The passwords don't match.");
    setBusy(true);
    try {
      const e = engine.value!;
      const c = cloud(dest);
      if (mode === "backup") {
        const bundle = await e.exportAllHistory();
        if (dest === "xmm") {
          const salt = crypto.getRandomValues(new Uint8Array(16));
          const key = await passwordKey(pw, salt, BACKUP_ITERATIONS);
          const up = await api.upload(await seal(key, bundle), "backup.bin");
          const r = await api.putBackup({ media_id: up.id, salt: btoa(String.fromCharCode(...salt)), iterations: BACKUP_ITERATIONS, messages: bundle.messages.length });
          setInfo(r.backup);
        } else {
          const { blob, header } = await packBackup(pw, bundle);
          if (c) {
            await c.upload(account, blob, { messages: header.messages, created_at: header.created_at });
            setCloudInfo((x) => ({ ...x, [c.kind]: { messages: header.messages, created_at: header.created_at, size: blob.size } }));
          } else await saveBackupFile(blob, `xmm-backup-${(getSession()?.user.name || account.split("@")[0]).replace(/[^\w-]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.xmmb`);
        }
        toast(dest === "file" ? "Backup file saved" : `Backed up to ${c?.label ?? "xmm"}`);
      } else {
        let bundle;
        if (dest === "xmm") {
          const salt = Uint8Array.from(atob(info!.salt), (ch) => ch.charCodeAt(0));
          bundle = await unseal(await passwordKey(pw, salt, info!.iterations), await (await fetch(info!.url)).arrayBuffer());
        } else bundle = await unpackBackup(pw, file ?? (await c!.download(account)));
        toast(`Restored ${await e.importHistory(bundle)} messages`);
        void e.dismissRestoreOffer();
      }
      close();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  const when = (i: { created_at: number; messages: number } | null | undefined) =>
    i === undefined ? "" : i ? `Last backup ${new Date(i.created_at * 1000).toLocaleString()} · ${i.messages} messages` : "No backup yet";
  const dests: { dest: Dest; label: string; icon: string; sub: string; restore: boolean }[] = [
    { dest: "xmm", label: "xmm server", icon: "lock", sub: info === undefined ? "Checking…" : when(info), restore: !!info },
    ...cl.map((c) => ({ dest: c.kind, label: c.label, icon: "download", sub: when(cloudInfo[c.kind]) || "Sign in to back up or restore", restore: cloudInfo[c.kind] !== null })),
    { dest: "file" as Dest, label: "Backup file", icon: "document", sub: "Save it anywhere: iCloud Drive, Google Drive, your computer", restore: true },
  ];
  const label = (d: Dest) => dests.find((x) => x.dest === d)?.label ?? "";

  return (
    <div class="drawer" role="dialog" aria-label="Chat backup">
      <header class="drawer-head">
        <button class="icon-btn" onClick={() => (drawer.value = "none")} aria-label="Back">
          <Icon name="back" />
        </button>
        <h2>Chat backup</h2>
      </header>
      <div class="drawer-body">
        <p class="muted pad">
          Back up your messages, end-to-end encrypted with a password only you know: on the xmm server, in your own Google Drive or iCloud, or as a
          file. If you forget the password, the backup can't be recovered, not even by xmm.
        </p>
        <input ref={fileInput} type="file" accept=".xmmb,application/octet-stream" hidden onChange={(e) => (void pickFile(e.currentTarget.files?.[0]), (e.currentTarget.value = ""))} />
        {!target && (
          <ul class="backup-dests">
            {dests.map((d) => (
              <li key={d.dest} class="backup-dest">
                <span class="list-action-icon">
                  <Icon name={d.icon as "lock" | "download" | "document"} />
                </span>
                <div class="grow">
                  <div class="chat-name">{d.label}</div>
                  <div class="muted small">{d.sub}</div>
                </div>
                <button class="chip" onClick={() => void start(d.dest, "backup")} aria-label={`Back up to ${d.label}`}>
                  Back up
                </button>
                {d.restore && (
                  <button
                    class="chip"
                    onClick={() => (d.dest === "file" ? fileInput.current?.click() : void start(d.dest, "restore"))}
                    aria-label={`Restore from ${d.label}`}
                  >
                    Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {!target && info && (
          <button class="list-action danger" onClick={() => void api.deleteBackup().then(load, (e) => toast(errText(e)))}>
            <span class="list-action-icon">
              <Icon name="trash" />
            </span>
            Delete the xmm server backup
          </button>
        )}
        {cl.some((c) => c.kind === "icloud") && <div id={APPLE_SIGN_IN_ID} class="apple-sign-in" hidden={target?.dest !== "icloud"} />}
        {target && (
          <form
            class="backup-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <p>
              <strong>{target.mode === "backup" ? `Back up to ${label(target.dest)}` : `Restore from ${label(target.dest)}`}</strong>
            </p>
            <input class="line-input" type="password" value={pw} onInput={(e) => setPw(e.currentTarget.value)} placeholder="Backup password" aria-label="Backup password" autoFocus />
            {target.mode === "backup" && (
              <input class="line-input" type="password" value={pw2} onInput={(e) => setPw2(e.currentTarget.value)} placeholder="Confirm password" aria-label="Confirm backup password" />
            )}
            {error && <p class="error">{error}</p>}
            <div class="row-buttons">
              <button type="button" class="link" onClick={close}>
                Cancel
              </button>
              <button class="primary" disabled={busy || !pw}>
                {busy ? (target.mode === "backup" ? "Encrypting…" : "Restoring…") : target.mode === "backup" ? "Back up" : "Restore"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Offer a restore on a device that has no chats yet. */
export function RestoreBanner() {
  const [info, setInfo] = useState<BackupInfo | null>(null);
  const [hidden, setHidden] = useState(false);
  const empty = S.chats.value.length === 0;
  // A new main phone is always offered a restore (Drive / iCloud / a file),
  // even without an xmm backup.
  const offer = S.offerRestore.value;
  useEffect(() => {
    if (empty || offer) void api.backup().then((r) => setInfo(r.backup), () => {});
  }, [empty, offer]);
  // (Offered on a new main phone until restored or dismissed, even once new
  // chats have started.)
  if (hidden || !(offer || (empty && info))) return null;
  return (
    <div class="restore-banner">
      <Icon name="refresh" />
      <div class="grow">
        <strong>Restore your chat history?</strong>
        <small>
          {info
            ? `A backup from ${new Date(info.created_at * 1000).toLocaleDateString()} is available.`
            : "Your chats come back from a backup: Google Drive, iCloud or a backup file."}
        </small>
      </div>
      <button class="link" onClick={() => (drawer.value = "backup")}>
        Restore
      </button>
      <button class="icon-btn small" aria-label="Dismiss" onClick={() => (setHidden(true), void engine.value?.dismissRestoreOffer())}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

// ---- history on a new device ------------------------------------------------------------

/** On a new device: where its chat history is. */
export function HistoryBanner() {
  const st = S.historyState.value;
  if (st !== "waiting" && st !== "importing") return null;
  const what = isPhone(nativePlatform) ? "Your chat history comes" : `The last ${HISTORY_DAYS} days come`;
  return (
    <div class="restore-banner">
      <Icon name="devices" />
      <div class="grow">
        <strong>{st === "importing" ? "Restoring your chat history…" : "Getting your chat history"}</strong>
        <small>{st === "importing" ? "Decrypting messages from your other device." : `${what} automatically from your other devices as soon as one of them is online.`}</small>
      </div>
      {st === "waiting" && (
        <button class="link" onClick={() => void engine.value?.skipHistory()}>
          Skip
        </button>
      )}
    </div>
  );
}
